import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, fmtUSD, fmtPct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const statusBadge = (s) =>
  s === "green" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
  s === "yellow" ? "bg-amber-100 text-amber-800 border-amber-200" :
  "bg-red-100 text-red-800 border-red-200";

const statusFor = (e) => e >= 20 ? "green" : e >= 5 ? "yellow" : "red";

export default function SitesList() {
  const [sites, setSites] = useState([]);
  const [pnls, setPnls] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", pi_name: "", clinic_name: "", stryde_contact: "" });

  const load = async () => {
    const s = await api.get("/sites").then((r) => r.data);
    setSites(s);
    const res = await Promise.all(s.map((x) => api.get(`/pnl/site/${x.id}`).then((r) => [x.id, r.data])));
    const map = {};
    res.forEach(([id, p]) => (map[id] = p));
    setPnls(map);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.pi_name) { toast.error("Name and PI required"); return; }
    await api.post("/sites", form);
    toast.success("Site created");
    setOpen(false);
    setForm({ name: "", pi_name: "", clinic_name: "", stryde_contact: "" });
    load();
  };

  return (
    <AppShell
      title="Sites & P&L"
      subtitle="Live profitability per site after PI distribution, clinic fees, and labor"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0A4C95] hover:bg-[#083d79]" data-testid="new-site-btn"><Plus className="w-4 h-4 mr-1" /> New Site</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Site</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Site Name</Label><Input data-testid="site-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>PI Name</Label><Input value={form.pi_name} onChange={(e) => setForm({ ...form, pi_name: e.target.value })} /></div>
              <div><Label>Clinic Name</Label><Input value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} /></div>
              <div><Label>Stryde Contact</Label><Input value={form.stryde_contact} onChange={(e) => setForm({ ...form, stryde_contact: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} data-testid="site-save-btn" className="bg-[#0A4C95] hover:bg-[#083d79]">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
        <table className="w-full text-sm stryde-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Site</th>
              <th className="text-left px-4 py-2.5">PI</th>
              <th className="text-left px-4 py-2.5">Clinic</th>
              <th className="text-right px-4 py-2.5">Gross Revenue</th>
              <th className="text-right px-4 py-2.5">Net Profit</th>
              <th className="text-right px-4 py-2.5">EBITDA</th>
              <th className="text-right px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => {
              const p = pnls[s.id] || {};
              const e = p.ebitda_percent || 0;
              return (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`site-row-${s.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 text-slate-700">{s.pi_name}</td>
                  <td className="px-4 py-3 text-slate-500">{s.clinic_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-fin">{fmtUSD(p.gross_revenue)}</td>
                  <td className="px-4 py-3 text-right font-mono-fin">{fmtUSD(p.net_profit)}</td>
                  <td className="px-4 py-3 text-right font-mono-fin font-semibold">{fmtPct(e)}</td>
                  <td className="px-4 py-3 text-right"><Badge variant="outline" className={statusBadge(statusFor(e))}>{statusFor(e).toUpperCase()}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/sites/${s.id}`}><Button size="sm" variant="outline" data-testid={`view-site-${s.id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" /> View P&L</Button></Link>
                  </td>
                </tr>
              );
            })}
            {sites.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500">No sites yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
